import React from 'react';
import { Images } from 'utils';
import { ManageActions } from 'components';
import { checkPrevNext, renderCardColor, renderColorValue } from '../session/core/sessionCards/constants';
import { PinnedGoalHeader } from '../session/core/sessionPinnedTargets';
import { isTimedType, manualTrialSummary } from './constants';
import { useManualTrialEditor } from './useManualTrialEditor';

/**
 * One pinned target, drawn like the live `SessionTargetGridCard`: a trial counter badge, the
 * target's running value, and the target name as a button that opens the same manual input the
 * list row uses (`useManualTrialEditor`) in a popover. Nothing is saved until Save Data.
 */
const ManualPinnedCard = ({ goal, target, trials, onChange, errorTrial }) => {
    const { index, setIndex, total, currentComplete, widgetCurrent, renderInput } = useManualTrialEditor({
        goal,
        target,
        trials,
        onChange,
        errorTrial,
    });
    // The inputs live in a closed popover, so the card itself shows which target Save rejected.
    const hasError = errorTrial !== null && errorTrial !== undefined;
    const navigable = checkPrevNext(goal);
    const { color, background } = renderCardColor(renderColorValue(goal, widgetCurrent));
    // The orange time badge is for timed targets only, as on the live grid card.
    const summary = isTimedType(goal?.dataType) ? manualTrialSummary(goal, trials) || '0s' : null;

    return (
        <div className="flex-align-start" id={`manual-pinned-${target?.id}`}>
            <div className="flex-all-center column">
                <div className="flex-align-center full-width" style={{ gap: 8, justifyContent: 'center', height: 11 }}>
                    {navigable && (
                        <div className="task-card-complete-number" style={{ background }}>
                            {/* The trial on screen, as the list row counts it — not how many trials exist. */}
                            <p style={{ color }}>{`${index + 1}/${total}`}</p>
                        </div>
                    )}
                    {summary && (
                        <div className="task-card-timer-box">
                            <p>{summary}</p>
                        </div>
                    )}
                </div>

                <div className={`task-card-wrapper ${hasError ? 'manual-pinned-error' : ''}`}>
                    {navigable ? (
                        <button
                            type="button"
                            disabled={index === 0}
                            className="prev-next-btn"
                            onClick={() => setIndex(index - 1)}
                            style={{ transform: 'rotate(90deg)', opacity: index === 0 ? 0.5 : 1 }}
                        >
                            <img src={Images.chevronDown} alt="previous trial" />
                        </button>
                    ) : (
                        <div />
                    )}

                    <ManageActions
                        btnTitle={target?.label || target?.name}
                        renderBody={() => <div className="manual-pinned-popover">{renderInput()}</div>}
                    />

                    {navigable ? (
                        <button
                            type="button"
                            disabled={!currentComplete}
                            onClick={() => setIndex(index + 1)}
                            className="prev-next-btn"
                            style={{
                                opacity: currentComplete ? 1 : 0.5,
                                transform: 'rotate(-90deg)',
                                filter: 'brightness(0) saturate(100%) invert(25%) sepia(100%) saturate(3259%) hue-rotate(209deg) brightness(95%) contrast(91%)',
                            }}
                        >
                            <img src={Images.chevronDown} alt="next trial" />
                        </button>
                    ) : (
                        <div />
                    )}
                </div>
            </div>
        </div>
    );
};

/** The pinned-goals strip of the manual screen — same classes as the live `SessionPinnedTargets`. */
export const ManualPinnedTargets = ({ goals, clientId, entries, entryError, onTargetChange }) => {
    const pinnedGoals = goals.filter((i) => i?.pinned && i?.goal?.targets?.length);
    if (!pinnedGoals.length) return null;

    return (
        <div style={{ margin: '12px 0' }}>
            <div className="goal-selector-wrapper widget-cards-box">
                {pinnedGoals.map(({ goal }) => (
                    <div key={goal?.id} className="pinned-goals-box">
                        <PinnedGoalHeader goal={goal} requestParams={{ clientId }} clientId={clientId} />
                        <div className="pinned-goal-targets">
                            {goal.targets.map((target) => (
                                <ManualPinnedCard
                                    key={target?.id}
                                    goal={goal}
                                    target={target}
                                    trials={entries[target?.id] || []}
                                    onChange={(trials) => onTargetChange(target?.id, trials)}
                                    errorTrial={entryError?.targetId === target?.id ? entryError.trialIndex : null}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
